import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import StatusBadge from "@/components/StatusBadge";
import CompanyDiscover from "@/components/CompanyDiscover";
import {
  FileText, Handshake, CalendarCheck, TrendDown, ChatsCircle, PlusCircle, MagnifyingGlass,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-6 bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={26} weight="duotone" color={accent || "#002FA7"} />
      <div className="font-display font-black text-3xl text-[#111827] mt-3">{value}</div>
      <div className="text-sm text-[#4B5563] mt-1">{label}</div>
    </div>
  );
}

function BookingDialog({ contract, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    guest_name: "", check_in: contract.start_date, check_out: contract.end_date, rooms: 1,
  });
  const mut = useMutation({
    mutationFn: (body) => api.createCompanyBooking(body),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Booking confirmed"); setOpen(false); onDone?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Booking failed"),
  });
  const F = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`book-btn-${contract.id}`}
          className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center gap-2">
          <CalendarCheck size={16} weight="bold" /> Book
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Booking</DialogTitle></DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">{contract.hotel_name} · {money(contract.negotiated_rate)}/nt</p>
        <label className="text-sm">
          <span className="overline text-[#4B5563] block mb-1">Guest / crew reference</span>
          <input data-testid="booking-guest" value={form.guest_name} onChange={F("guest_name")}
            placeholder="e.g. Pipeline Crew A" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Check-in</span>
            <input data-testid="booking-checkin" type="date" value={form.check_in} onChange={F("check_in")}
              className="w-full border border-[#E5E7EB] px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Check-out</span>
            <input data-testid="booking-checkout" type="date" value={form.check_out} onChange={F("check_out")}
              className="w-full border border-[#E5E7EB] px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Rooms</span>
            <input data-testid="booking-rooms" type="number" min="1" value={form.rooms} onChange={F("rooms")}
              className="w-full border border-[#E5E7EB] px-2 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        </div>
        <DialogFooter>
          <button data-testid="confirm-booking-btn" disabled={mut.isPending}
            onClick={() => { if (!form.guest_name.trim()) return toast.error("Add a guest/crew reference");
              mut.mutate({ contract_id: contract.id, guest_name: form.guest_name, check_in: form.check_in, check_out: form.check_out, rooms: Number(form.rooms) }); }}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Booking…" : "Confirm Booking"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "discover", label: "Find Hotels by Area" },
  { id: "rfps", label: "My RFPs" },
  { id: "contracts", label: "Contracts" },
  { id: "bookings", label: "Bookings" },
];

export default function CompanyDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const qc = useQueryClient();

  const { data: a } = useQuery({ queryKey: ["cAnalytics"], queryFn: api.companyAnalytics });
  const { data: rfps = [] } = useQuery({ queryKey: ["rfps", "company"], queryFn: () => api.rfps() });
  const { data: contracts = [] } = useQuery({ queryKey: ["contracts", "company"], queryFn: () => api.contracts() });
  const { data: bookings = [] } = useQuery({ queryKey: ["bookings", "company"], queryFn: () => api.bookings() });

  const cancel = useMutation({
    mutationFn: (id) => api.cancelBooking(id),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Booking cancelled"); },
  });

  const setContract = useMutation({
    mutationFn: ({ id, status }) => api.setContractStatus(id, status),
    onSuccess: (_, { status }) => { qc.invalidateQueries(); toast.success(`Contract ${status}`); },
  });

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="overline text-[#002FA7]">Company workspace</span>
          <h1 className="font-display font-black text-4xl text-[#111827] mt-2">{user.company_name || user.name}</h1>
        </div>
        <button onClick={() => setTab("discover")} data-testid="new-rfp-cta"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]">
          <MagnifyingGlass size={18} weight="bold" /> Find hotels by area
        </button>
      </div>

      <div className="flex gap-1 border-b border-[#E5E7EB] mt-8">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#4B5563] hover:text-[#111827]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 grid-skeleton border-l border-t border-[#E5E7EB]">
            <Stat icon={FileText} label="RFPs Sent" value={a?.rfps_sent ?? 0} />
            <Stat icon={ChatsCircle} label="Active Negotiations" value={a?.active_negotiations ?? 0} accent="#D97706" />
            <Stat icon={Handshake} label="Contracts Signed" value={a?.contracts_signed ?? 0} accent="#047857" />
            <Stat icon={CalendarCheck} label="Bookings" value={a?.bookings ?? 0} />
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="border border-[#111827] p-8">
              <TrendDown size={30} weight="duotone" color="#047857" />
              <div className="font-display font-black text-4xl text-[#047857] mt-3">{money(a?.est_savings)}</div>
              <div className="text-sm text-[#4B5563] mt-1">Estimated savings vs. target budget on signed programs</div>
            </div>
            <div className="border border-[#E5E7EB] p-8">
              <CalendarCheck size={30} weight="duotone" color="#002FA7" />
              <div className="font-display font-black text-4xl text-[#111827] mt-3">{money(a?.total_spend)}</div>
              <div className="text-sm text-[#4B5563] mt-1">Total committed spend across confirmed bookings</div>
            </div>
          </div>
        </div>
      )}

      {tab === "discover" && <CompanyDiscover />}

      {tab === "rfps" && (
        <div className="mt-8 space-y-3" data-testid="rfp-list">
          {rfps.length === 0 && <Empty text="No RFPs yet. Find a hotel to submit your first proposal." />}
          {rfps.map((r) => (
            <Link key={r.id} to={`/negotiation/${r.id}`} data-testid={`rfp-row-${r.id}`}
              className="rb-card rb-card-hover flex items-center justify-between p-5">
              <div>
                <div className="font-display font-bold text-lg text-[#111827]">{r.hotel_name}</div>
                <div className="text-sm text-[#4B5563]">{r.city} · {r.num_rooms} rooms · {dateFmt(r.check_in)}–{dateFmt(r.check_out)}</div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right hidden sm:block">
                  <div className="font-mono font-semibold text-[#111827]">{money(r.nightly_budget)}/nt</div>
                  <div className="text-xs text-[#4B5563]">{r.room_nights} room-nights</div>
                </div>
                <StatusBadge status={r.status} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === "contracts" && (
        <div className="mt-8 space-y-3" data-testid="contract-list">
          {contracts.length === 0 && <Empty text="No contracts yet. Negotiate an RFP to receive one." />}
          {contracts.map((c) => (
            <div key={c.id} className="rb-card flex items-center justify-between p-5">
              <div>
                <div className="font-display font-bold text-lg text-[#111827]">{c.hotel_name}</div>
                <div className="text-sm text-[#4B5563]">{money(c.negotiated_rate)}/nt · {c.room_nights} room-nights · {dateFmt(c.start_date)}–{dateFmt(c.end_date)}</div>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge status={c.status} />
                {c.status === "sent" && (
                  <>
                    <button data-testid={`accept-contract-${c.id}`} onClick={() => setContract.mutate({ id: c.id, status: "accepted" })}
                      className="text-sm font-semibold text-[#047857] hover:underline">Accept</button>
                    <button data-testid={`decline-contract-${c.id}`} onClick={() => setContract.mutate({ id: c.id, status: "declined" })}
                      className="text-sm font-semibold text-[#DC2626] hover:underline">Decline</button>
                  </>
                )}
                {c.status === "accepted" && <BookingDialog contract={c} />}
                <Link to={`/negotiation/${c.rfp_id}`} className="text-sm font-semibold text-[#002FA7] hover:underline">View →</Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="mt-8 space-y-3" data-testid="booking-list">
          {bookings.length === 0 && <Empty text="No bookings yet. Accept a contract, then book against it." />}
          {bookings.map((b) => (
            <div key={b.id} className="rb-card flex items-center justify-between p-5" data-testid={`booking-row-${b.id}`}>
              <div>
                <div className="font-display font-bold text-lg text-[#111827]">{b.hotel_name}</div>
                <div className="text-sm text-[#4B5563]">{b.guest_name} · {b.rooms} rooms · {dateFmt(b.check_in)}–{dateFmt(b.check_out)}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right hidden sm:block">
                  <div className="font-mono font-semibold text-[#111827]">{money(b.total)}</div>
                  <div className="text-xs text-[#4B5563]">{money(b.rate)}/nt</div>
                </div>
                <StatusBadge status={b.status} />
                {b.status === "confirmed" && (
                  <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancel.mutate(b.id)}
                    className="text-sm font-semibold text-[#DC2626] hover:underline">Cancel</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="border border-dashed border-[#E5E7EB] py-16 text-center">
      <PlusCircle size={32} weight="duotone" color="#9CA3AF" className="mx-auto" />
      <p className="text-[#4B5563] mt-3">{text}</p>
    </div>
  );
}
